<template>
  <div class="view-startup-find view-dark-bg">
    <b-container>
      <b-row>
        <b-col class="pt-10 pb-20 py-lg-40">
          <SearchBar
            v-model="searchForm"
            search-placeholder="Seach by name or keyword"
            location-placeholder="Choose location"
            light-shadow
            disable-country-selector
            :auto-suggest-country-codes="filters.countryCodes"
            :on-submit="onSearchBarSubmit"
          />
        </b-col>
      </b-row>
      <b-row ref="list" class="pb-60">
        <b-col lg="3" @mouseleave="handleFiltersMouseLeave">
          <SearchFilter
            :isLoading="isLoading"
            v-model="filters.countryCodes"
            label="Countries"
            label-key="text"
            :options="countries"
          />
          <SearchFilter
            :isLoading="isLoading"
            v-model="filters.industries"
            label="Industry"
            :options="industries"
          />
          <SearchFilter
            :isLoading="isLoading"
            v-model="filters.stage"
            label="Startup stage"
            :options="companyStages"
          />
          <SearchFilter
            :isLoading="isLoading"
            v-model="filters.numberOfEmployees"
            label="Startup size"
            :options="numberOfEmployeesOptions"
          />
          <SearchFilter
            :isLoading="isLoading"
            v-model="filters.funding"
            label="Funding"
            :options="companyFunding"
          />
          <SearchFilter
            :isLoading="isLoading"
            v-model="filters.sdgs"
            label="Impact startup SDGs"
            :options="sdgs"
            :add-numbers="true"
            :display-limit="0"
            collapsible
            collapsed
          />
        </b-col>
        <b-col lg="9">
          <header class="mt-16 mt-lg-0 pt-10 pt-lg-0 pb-4 pb-lg-0 border-md-down-top">
            <b-row class="mt-16 mt-lg-0 mb-16 mb-lg-30">
              <b-col class="d-flex align-items-center">
                <h6>
                  <span class="hidden-md-down">Showing:</span>
                  {{ startups ? startups.total : 0 }}
                  <span class="hidden-md-down">filtered</span> startups
                </h6>
              </b-col>
            </b-row>
          </header>
          <section v-if="isLoading" class="startup_skeletons">
            <!-- <section class="startup_skeletons" v-if="true"> -->
            <div v-for="skeleton in skeletons" :key="skeleton.id" class="startup_skeleton-wrapper">
              <div class="startup_skeleton-info">
                <div class="startup_skeleton-header">
                  <VueSkeletonLoader :width="72" :height="72" animation="fade" />
                  <VueSkeletonLoader :width="28" :height="28" animation="fade" :rounded="true" />
                </div>

                <div class="startup_skeleton-titles">
                  <VueSkeletonLoader
                    :width="70"
                    :height="30"
                    animation="fade"
                    :rounded="true"
                    style="margin-bottom: 1.5rem;"
                  />
                  <VueSkeletonLoader
                    :height="12"
                    animation="fade"
                    :rounded="true"
                    class="startup_skeleton-titles-small_title"
                  />
                  <VueSkeletonLoader
                    :height="12"
                    animation="fade"
                    :rounded="true"
                    class="startup_skeleton-titles-small_title"
                  />
                  <VueSkeletonLoader
                    :height="12"
                    animation="fade"
                    :rounded="true"
                    class="startup_skeleton-titles-small_title"
                  />
                  <VueSkeletonLoader
                    :height="12"
                    animation="fade"
                    :rounded="true"
                    class="startup_skeleton-titles-small_title"
                    style="margin-bottom:2rem"
                  />
                  <VueSkeletonLoader
                    :width="70"
                    :height="20"
                    animation="fade"
                    :rounded="true"
                    style="margin-bottom: 1rem;"
                  />
                  <VueSkeletonLoader :width="90" :height="36" animation="fade" :rounded="false" />
                </div>
              </div>
            </div>
          </section>
          <content v-else>
            <!-- <content> -->
            <SearchNoResults v-if="!list.length" type="startups" />
            <b-row v-else>
              <b-col v-for="startup in list" :key="startup.id" lg="4" md="6" class="mb-30">
                <StartupsCard :startup="startup" />
              </b-col>
            </b-row>
          </content>
          <footer v-if="startups && startups.pages > 1" class="mt-40">
            <SearchPagination v-model="page" :result="startups" />
          </footer>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import omitBy from 'lodash/omitBy';
import castArray from 'lodash/castArray';
import get from 'lodash/get';
import clone from 'lodash/clone';
import head from 'lodash/head';
import SearchBar from '@/components/search/SearchBar';
import SearchFilter from '@/components/search/SearchFilter';
import StartupsCard from '@/components/startups/StartupsCard';
import SearchNoResults from '@/components/search/SearchNoResults';
import SearchPagination from '@/components/search/SearchPagination';
import VueSkeletonLoader from 'skeleton-loader-vue';
import { isEmptyDeep } from '@/helpers/helpers';
import { buildMetaHead } from '@/helpers/meta';
import AbortController from 'abort-controller';

export default {
  name: 'PageStartups',
  components: {
    SearchBar,
    SearchFilter,
    StartupsCard,
    SearchPagination,
    SearchNoResults,
    VueSkeletonLoader
  },
  head() {
    return buildMetaHead({
      title: 'Browse among 9,000+ startups to find your dream job',
      description:
        'Find your next purposeful and exciting startup job among 9,000+ recruiting Nordic startups and scaleups. Everything from co-founder, intern to senior positions',
      path: this.$route.path,
      imageUrl: process.env.BASE_URL + '/images/thehub_2022_find_startups.jpg'
    });
  },
  data() {
    const countryCode = this.$store.getters['meta/sitePreferredCountryCode'];
    // console.log('this.activeCountries', this.activeCountries);
    const activeCountries = this.$store.getters['meta/siteCountriesExNI']
      .filter((item) => !!item.value)
      .filter((item) => item.value.toLowerCase() !== 'eu')
      .filter((item) => item.value.toLowerCase() !== 'remote')
      .map((item) => item.value);
    // console.log('activeCountries', activeCountries);
    // console.log('this.$route', this.$route);
    return {
      isSearchTimeoutClear: true,
      searchTimeout: null,
      page: parseInt(get(this.$route, 'query.page', 1)),
      filters: {
        industries: castArray(get(this.$route, 'query.industries', [])),
        stage: castArray(get(this.$route, 'query.stage', [])),
        funding: castArray(get(this.$route, 'query.funding', [])),
        numberOfEmployees: castArray(get(this.$route, 'query.numberOfEmployees', [])),
        search: get(this.$route, 'query.search'),
        location: get(this.$route, 'query.location'),
        // countryCodes: castArray(get(this.$route, 'query.countryCodes', [countryCode])),
        countryCodes:
          this.$route &&
          this.$route.query &&
          this.$route.query.countryCodes &&
          this.$route.query.countryCodes.length > 0
            ? castArray(get(this.$route, 'query.countryCodes', [countryCode]))
            : activeCountries,
        sdgs: castArray(get(this.$route, 'query.sdgs', []))
      },
      searchForm: {
        search: get(this.$route, 'query.search'),
        location: get(this.$route, 'query.location')
      },
      industryStats: [],
      isLoading: false
    };
  },
  computed: {
    skeletons() {
      return Array.from({ length: 15 }, (_, i) => ({ id: i }));
    },
    params() {
      const filters = omitBy(clone(this.filters), isEmptyDeep);
      if (this.page >= 2) {
        filters.page = this.page;
      }

      return filters;
    },
    startups() {
      return this.$store.state.startups.startups;
    },
    list() {
      return this.startups ? this.startups.docs : [];
    },
    // activeCountries() {
    //   return this.$store.getters['meta/siteCountriesExNI']
    //     .filter((item) => !!item.value)
    //     .filter((item) => item.value.toLowerCase() !== 'eu')
    //     .filter((item) => item.value.toLowerCase() !== 'remote')
    //     .map((item) => item.value);
    // },
    countries() {
      // console.log('countries', this.$store.getters['meta/siteCountriesExNI']);
      return this.$store.getters['meta/siteCountriesExNI']
        .filter((item) => item.value !== 'EU')
        .map((item) => ({
          ...item,
          count: get(this.startups, 'suggestions.countries.' + item.value.toLowerCase(), undefined)
        }));
    },
    sdgs() {
      return this.$store.getters['meta/sdgs'].map((item) => ({
        ...item,
        count: get(this.startups, 'suggestions.sdgs.' + item.value, undefined)
      }));
    },
    industries() {
      return this.$store.getters['meta/industries']
        .filter((item) => {
          const stats = this.industryStats.find((stat) => stat.industry === item.value);

          return stats && stats.exists;
        })
        .sort((a, b) => (a.label > b.label ? 1 : -1))
        .map((item) => ({
          ...item,
          count: get(this.startups, 'suggestions.industries.' + item.value, undefined)
        }));
    },
    companyStages() {
      return this.$store.getters['meta/companyStages'].map((item) => ({
        ...item,
        count: get(this.startups, 'suggestions.stages.' + item.value, undefined)
      }));
    },
    companyFunding() {
      return this.$store.getters['meta/companyFunding'].map((item) => ({
        ...item,
        count: get(this.startups, 'suggestions.funding.' + item.value, undefined)
      }));
    },
    numberOfEmployeesOptions() {
      return this.$store.getters['meta/companyNumberOfEmployees'].map((item) => ({
        ...item,
        count: get(this.startups, 'suggestions.numberOfEmployees.' + item.value, undefined)
      }));
    }
  },
  watch: {
    'filters.countryCodes'(newValue, oldValue) {
      /**
       * Whenever we change countries, reset the `location` value.
       */
      if (newValue !== oldValue) {
        this.searchForm.location = undefined;
        this.filters.location = undefined;

        this.$store.dispatch('meta/setPreferredCountryCode', head(newValue));
      }
    },
    filters: {
      deep: true,
      async handler() {
        this.page = 1;
        // await this.doSearch();
        this.scheduleSearch();
      }
    },
    page() {
      // this.doSearch();
      this.scheduleSearch();

      this.scrollToTop();
    }
  },
  // async asyncData({ $axios, query, store, error }) {
  //   try {
  //     const params = {
  //       includeSuggestions: true,
  //       ...query
  //     };

  //     if (!params.countryCodes) {
  //       // // if no `countryCodes` defined, default to ip detected.
  //       // const countryCode = store.getters['meta/sitePreferredCountryCode'];
  //       // params.countryCodes = [countryCode];

  //       const activeCountries = store.getters['meta/siteCountriesExNI']
  //         .filter((item) => !!item.value)
  //         .filter((item) => item.value.toLowerCase() !== 'eu')
  //         .filter((item) => item.value.toLowerCase() !== 'remote')
  //         .map((item) => item.value);

  //       params.countryCodes = activeCountries;
  //     }

  //     // console.log(params);

  //     const [industryStats] = await Promise.all([
  //       $axios.$get('companies/industries'),
  //       store.dispatch('startups/find', params)
  //     ]);

  //     return {
  //       industryStats
  //     };
  //   } catch (err) {
  //     error(err);
  //   }
  // },
  async asyncData({ $axios, query, store, error }) {
    const controller = new AbortController();
    const { signal } = controller;

    try {
      const params = {
        includeSuggestions: true,
        ...query
      };

      if (!params.countryCodes) {
        const activeCountries = store.getters['meta/siteCountriesExNI']
          .filter((item) => !!item.value)
          .filter((item) => item.value.toLowerCase() !== 'eu')
          .filter((item) => item.value.toLowerCase() !== 'remote')
          .map((item) => item.value);

        params.countryCodes = activeCountries;
      }

      const [industryStats] = await Promise.all([
        $axios.$get('companies/industries', { signal }),
        store.dispatch('startups/find', params)
      ]);

      return {
        industryStats
      };
    } catch (err) {
      if (err.name !== 'AbortError') {
        error(err);
      }
    }

    this.$once('hook:beforeDestroy', () => {
      controller.abort();
    });
  },
  methods: {
    scrollToTop() {
      if (this.$refs.list && this.$refs.list.scrollIntoView) {
        document.body.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    },
    onSearchBarSubmit() {
      this.filters.search = this.searchForm.search;
      this.filters.location = this.searchForm.location;

      this.doSearch();
    },

    // async doSearch() {
    //   const asyncSearch = async () => {
    //     await this.$store.dispatch('startups/find', {
    //       includeSuggestions: true,
    //       ...this.params
    //     });

    //     this.$router.push({
    //       query: this.params
    //     });
    //   };

    //   this.isLoading = true;
    //   setTimeout(async () => {
    //     await asyncSearch();
    //     this.isLoading = false;
    //   }, 0);
    // }
    handleFiltersMouseLeave() {
      if (!this.isSearchTimeoutClear) {
        clearTimeout(this.searchTimeout);
        this.doSearch();
      }
    },
    scheduleSearch() {
      clearTimeout(this.searchTimeout);
      this.isSearchTimeoutClear = true;

      this.searchTimeout = setTimeout(() => {
        this.doSearch();
      }, 800);
      this.isSearchTimeoutClear = false;
    },
    async doSearch() {
      let timeoutId;

      const asyncSearch = async () => {
        await this.$store.dispatch('startups/find', {
          includeSuggestions: true,
          ...this.params
        });

        this.$router.push({
          query: this.params
        });
      };

      this.isLoading = true;
      this.isSearchTimeoutClear = true;
      timeoutId = setTimeout(async () => {
        await asyncSearch();
        this.isLoading = false;
      }, 0);

      this.$once('hook:beforeDestroy', () => {
        clearTimeout(timeoutId);
      });
    }
  },
  beforeDestroy() {
    clearTimeout(this.searchTimeout);
  }
};
</script>

<style lang="scss">
.startup_skeletons {
  display: grid;
  // grid-template-columns: repeat(3, 1fr);
  // grid-template-rows: repeat(5, 1fr);
  grid-column-gap: 1.875rem;
  grid-row-gap: 1.875rem;
  margin-bottom: 4rem;

  @media (max-width: 767px) {
    grid-template-columns: repeat(1, 1fr);
    grid-template-rows: repeat(15, 1fr);
  }

  @media (min-width: 767px) {
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: repeat(8, 1fr);
  }

  @media (min-width: 991px) {
    grid-template-columns: repeat(3, 1fr);
    grid-template-rows: repeat(5, 1fr);
  }
}

.startup_skeleton-wrapper {
  * {
    cursor: unset !important;
  }
  padding: 1.5rem;
  height: 372px;
  background-color: white;
  display: flex;
  align-items: flex-start;
  @media (max-width: 575px) {
    width: 100% !important;
  }
  @media (min-width: 575px) {
    width: 510px !important;
  }
  @media (min-width: 767px) {
    width: 330px !important;
  }

  @media (min-width: 991px) {
    width: 210px !important;
  }

  @media (min-width: 1199px) {
    width: 255px !important;
  }
}

.startup_skeleton-header {
  display: flex;
  width: 100%;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 2rem;
}

.startup_skeleton-info {
  width: 100%;
}

.startup_skeleton-titles {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-evenly;
  width: 100%;
}

.startup_skeleton-titles-small_title {
  width: 100% !important;
  margin-bottom: 0.25rem;
}

.filter_skeletons {
  @include media-breakpoint-down(md) {
    display: none;
  }
}

.filter_skeleton-header {
  display: flex;
  align-items: center;
  font-weight: 500;
  padding-bottom: 1rem;
  cursor: pointer;
}
.filter_skeleton-wrapper {
  * {
    cursor: unset !important;
  }
  margin-bottom: 1.25rem;
}
</style>
